import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { formatCurrency2SigFig, computeSpend, getEffectiveCpm } from '../../../../utils/campaigns';
import { formatCreativesExport, formatPublishersExport } from './exportColumns';
import { resolveCampaignDateRange } from '../../../../utils/campaignDateRange';

const TRANSACTIONS_CSV_FIELDS = [
  'Order ID',
  'Insertion Order',
  'Total Impressions',
  'Creative ID',
  'Revenue',
  'Transaction Date',
  'Complete First Impressions',
  'Impressions',
];
const TRANSACTIONS_CSV_CHUNK_SIZE = 1000;

const toTransactionCsvRow = (t) => ({
  'Order ID': t.orderId,
  'Insertion Order': t.adProviderId,
  'Total Impressions': t.totalImpressions,
  'Creative ID': [
    ...new Set((t.impressions || []).map((imp) => imp.creativeId).filter(Boolean)),
  ].join('; '),
  Revenue: t.revenue,
  'Transaction Date': t.transactionDate,
  'Complete First Impressions': JSON.stringify((t.impressions && t.impressions[0]) || null),
  Impressions: JSON.stringify(t.impressions || []),
});

// Builds and saves the "Transaction - With Events" zip for one set of rows.
// Extremely large campaigns produce a CSV larger than V8's max string length, so a
// single Papa.unparse(...) over the whole dataset throws "RangeError: Invalid string
// length". Build the CSV in row chunks and assemble a Blob (binary concat — never one
// giant string) so the export scales past that limit.
const downloadTransactionsWithEvents = async (rows, campaignOrderName) => {
  const csvParts = [Papa.unparse({ fields: TRANSACTIONS_CSV_FIELDS, data: [] })]; // header row only
  for (let i = 0; i < rows.length; i += TRANSACTIONS_CSV_CHUNK_SIZE) {
    const chunk = rows.slice(i, i + TRANSACTIONS_CSV_CHUNK_SIZE).map(toTransactionCsvRow);
    csvParts.push(
      '\r\n' + Papa.unparse({ fields: TRANSACTIONS_CSV_FIELDS, data: chunk }, { header: false })
    );
  }
  const csvBlob = new Blob(csvParts, { type: 'text/csv;charset=utf-8;' });

  const zip = new JSZip();
  zip.folder('Transaction - With Events').file(`${campaignOrderName} Transactions.csv`, csvBlob);
  const blob = await zip.generateAsync({ type: 'blob' });
  saveAs(blob, `${campaignOrderName} - Transactions`);
};

const TransactionsTabController = ({
  getImpressions,
  byDateData,
  creativesDataByDate,
  creativesData,
  publishersData,
  publishersDataByDate,
  transactionsData,
  details,
  campaignOrder,
  pagination,
  pageSize,
  setExportButtonMenuItems,
  setExportButtonLoading,
  getAllTransactions,
  allTransactionsData,
  allTransactionsError,
  transactionsScopeKey,
  setExportButtonDisable,
  // Individual loading states
  byDateLoading,
  creativesLoading,
  publishersLoading,
  transactionsLoading,
  detailsLoading,
  allTransactionsLoading,
  creativesByDateLoading,
  publishersByDateLoading,
  // Lazy query functions for on-demand loading with pagination
  fetchAllCreativesByDate,
  fetchAllPublishersByDate,
  // Campaign byDate lazy loading
  transactionsByCampaign,
  fetchCampaignByDate,
  campaignByDateLoading,
  // Per-campaign creatives/publishers lazy loading
  fetchCreativesByCampaign,
  fetchPublishersByCampaign,
  creativeByCampaignLoading,
  publisherByCampaignLoading,
  ...props
}) => {
  const history = useHistory();
  const location = useLocation();
  const [byDate, setByDate] = useState([]);
  const [byDayOfWeek, setByDayOfWeek] = useState([]);
  const [defaultSummaryDates, setDefaultSummaryDates] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({
    total: 0,
    hasNextPage: false,
  });
  const [detailBarSummary, setDetailBarSummary] = useState({
    transactions: 0,
    transactionRevenue: 0,
    customers: 0,
    spend: 0,
    impressions: 0,
  });
  const [exporting, setExporting] = useState(false);

  // Read URL params once
  const queryParams = new URLSearchParams(location.search);
  const startDateParam = queryParams.get('startDate');
  const endDateParam = queryParams.get('endDate');

  // Campaign tab state - read from URL params for filtering
  const campaignIndex = queryParams.get('campaignIndex') || 'All';

  // Attribution filters - read from URL params. Derived rather than held in
  // state so the first render with a filter in the URL already treats the
  // set as filtered instead of showing unfiltered numbers for a frame.
  const attributionWindow = parseInt(queryParams.get('attributionWindow')) || 30;
  const attributionMethod = queryParams.get('attributionMethod') || 'ALL';
  const attributionFiltered = attributionMethod !== 'ALL' || attributionWindow !== 30;
  const [selectedCampaignByDate, setSelectedCampaignByDate] = useState([]);

  // State for per-campaign creatives/publishers data
  const [selectedCampaignCreatives, setSelectedCampaignCreatives] = useState([]);
  const [selectedCampaignPublishers, setSelectedCampaignPublishers] = useState([]);

  // Per-campaign date ranges - keyed by campaign index (each campaign remembers its date range)
  const [campaignDateRanges, setCampaignDateRanges] = useState({});

  // Determine active date range based on campaign selection
  const activeDateRange = useMemo(() => {
    if (campaignIndex === 'All') {
      // Use URL params for "All" tab
      if (startDateParam && endDateParam) {
        return [moment(startDateParam), moment(endDateParam)];
      }
      return null;
    } else {
      // Use per-campaign state
      return campaignDateRanges[campaignIndex] || null;
    }
  }, [campaignIndex, startDateParam, endDateParam, campaignDateRanges]);

  // Get string versions for filtering logic
  const activeStartDate = activeDateRange?.[0]?.format('YYYY-MM-DD') || null;
  const activeEndDate = activeDateRange?.[1]?.format('YYYY-MM-DD') || null;

    // Function to update campaign index via URL params
  const setCampaignIndex = (index) => {
    const newParams = new URLSearchParams(location.search);
    if (index === 'All') {
      newParams.delete('campaignIndex');
    } else {
      newParams.set('campaignIndex', index);
    }
    // Restore saved date range for this tab (if any)
    const savedDateRange = campaignDateRanges[index];
    if (savedDateRange && savedDateRange.length === 2) {
      newParams.set('startDate', savedDateRange[0].format('YYYY-MM-DD'));
      newParams.set('endDate', savedDateRange[1].format('YYYY-MM-DD'));
    } else {
      // No saved date range - clear URL date params to show full range
      newParams.delete('startDate');
      newParams.delete('endDate');
    }
    newParams.set('page', '1'); // Reset to first page when changing campaign
    history.push({ search: newParams.toString() });
  };

  // Trigger lazy load of by-date data when date filter is applied
  useEffect(() => {
    if (activeStartDate && activeEndDate && fetchAllCreativesByDate && fetchAllPublishersByDate) {
      // Build where clause with date filters
      const where = {
        report: {
          analytics: {
            campaignOrder: {
              id: campaignOrder.id,
            },
          },
        },
        date_gte: activeStartDate,
        date_lte: activeEndDate,
      };

      // Fetch all pages of creatives by date (1000 records per page, automatically paginated)
      fetchAllCreativesByDate({
        where,
        first: 1000,
        orderBy: 'date_ASC',
      });

      // Fetch all pages of publishers by date (1000 records per page, automatically paginated)
      fetchAllPublishersByDate({
        where,
        first: 1000,
        orderBy: 'date_ASC',
      });
    }
  }, [
    activeStartDate,
    activeEndDate,
    campaignOrder.id,
    fetchAllCreativesByDate,
    fetchAllPublishersByDate,
  ]);

  // Trigger lazy load when campaign tab changes
  useEffect(() => {
    if (campaignIndex !== 'All' && transactionsByCampaign?.length > 0) {
      const selectedCampaign = transactionsByCampaign[parseInt(campaignIndex)];
      if (selectedCampaign?.id) {
        fetchCampaignByDate(selectedCampaign.id).then((data) => {
          setSelectedCampaignByDate(data);
        });
      }
    } else {
      // "All" tab uses existing byDateData
      setSelectedCampaignByDate(prev => prev.length === 0 ? prev : []);
    }
  }, [campaignIndex, transactionsByCampaign, fetchCampaignByDate]);

  // Trigger lazy load for per-campaign creatives/publishers when campaign tab changes
  useEffect(() => {
    if (campaignIndex !== 'All' && transactionsByCampaign?.length > 0) {
      const selectedCampaign = transactionsByCampaign[parseInt(campaignIndex)];
      if (selectedCampaign?.adProviderId) {
        fetchCreativesByCampaign(selectedCampaign.adProviderId).then((data) => {
          setSelectedCampaignCreatives(data);
        });

        fetchPublishersByCampaign(selectedCampaign.adProviderId).then((data) => {
          setSelectedCampaignPublishers(data);
        });
      }
    } else {
      // "All" tab uses existing aggregate data
      setSelectedCampaignCreatives(prev => prev.length === 0 ? prev : []);
      setSelectedCampaignPublishers(prev => prev.length === 0 ? prev : []);
    }
  }, [
    campaignIndex,
    transactionsByCampaign,
    fetchCreativesByCampaign,
    fetchPublishersByCampaign,
  ]);

  // Determine which byDate to use for charts
  const activeByDate = useMemo(() => {
    if (campaignIndex === 'All') {
      return byDateData; // Use existing aggregated byDate
    }
    return selectedCampaignByDate; // Use lazy-loaded campaign byDate
  }, [campaignIndex, byDateData, selectedCampaignByDate]);

  // Helper function to calculate summary from filtered data
  const calculateSummary = (data, startDate, endDate) => {
    const allUniqueIdentifiers = new Set();

    const summary = data.reduce(
      (acc, dateEntry) => {
        if (dateEntry.uniqueIdentifiers && Array.isArray(dateEntry.uniqueIdentifiers)) {
          dateEntry.uniqueIdentifiers.forEach((id) => allUniqueIdentifiers.add(id));
        }

        return {
          transactions: acc.transactions + (dateEntry.transactions || 0),
          transactionRevenue: acc.transactionRevenue + (dateEntry.revenue || 0),
          spend: acc.spend + (dateEntry.spend || 0),
          impressions: acc.impressions + (dateEntry.impressions || 0),
        };
      },
      {
        transactions: 0,
        transactionRevenue: 0,
        spend: 0,
        impressions: 0,
      }
    );

    summary.customers = allUniqueIdentifiers.size;
    summary.startDate = startDate;
    summary.endDate = endDate;

    return summary;
  };

  // Helper function to transform data into day-of-week groupings
  const calculateDayOfWeek = (data) => {
    const dayOfWeekMap = {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday',
    };

    const dayOfWeekData = {};

    data.forEach((dateEntry) => {
      const dayOfWeek = moment(dateEntry.date).day();
      const dayName = dayOfWeekMap[dayOfWeek];

      if (!dayOfWeekData[dayName]) {
        dayOfWeekData[dayName] = {
          dayOfWeek: dayName,
          transactions: 0,
          revenue: 0,
          spend: 0,
          customers: 0,
          impressions: 0,
          uniqueIdentifiers: new Set(),
        };
      }

      dayOfWeekData[dayName].transactions += dateEntry.transactions || 0;
      dayOfWeekData[dayName].revenue += dateEntry.revenue || 0;
      dayOfWeekData[dayName].spend += dateEntry.spend || 0;
      dayOfWeekData[dayName].impressions += dateEntry.impressions || 0;

      if (dateEntry.uniqueIdentifiers && Array.isArray(dateEntry.uniqueIdentifiers)) {
        dateEntry.uniqueIdentifiers.forEach((id) =>
          dayOfWeekData[dayName].uniqueIdentifiers.add(id)
        );
      }
    });

    const dayOfWeekArray = Object.keys(dayOfWeekMap).map((dayNum) => {
      const dayName = dayOfWeekMap[dayNum];
      const data = dayOfWeekData[dayName];

      if (!data) {
        return {
          dayOfWeek: dayName,
          transactions: 0,
          revenue: 0,
          spend: 0,
          customers: 0,
          impressions: 0,
        };
      }

      return {
        dayOfWeek: dayName,
        transactions: data.transactions,
        revenue: data.revenue,
        spend: data.spend,
        customers: data.uniqueIdentifiers.size,
        impressions: data.impressions,
      };
    });

    return dayOfWeekArray;
  };

  useEffect(() => {
    let byDateToBeProcessed = activeByDate;
    let creaivesToBeProcessed = creativesDataByDate;
    let publisherToBeProcessed = publishersDataByDate;

    // An empty set is a completed fetch that nothing survived, not a missing
    // one: run the recompute so the card shows zeros rather than the rollup.
    if (attributionFiltered && allTransactionsData) {
      // Seed byDateMap from activeByDate (which is scope-aware: aggregate when
      // campaignIndex === 'All', otherwise the selected campaign's byDate) so every
      // day with impressions/spend for the current scope is represented, even if no
      // transactions survive the attribution filter.
      const byDateMap = {};
      if (activeByDate) {
        activeByDate.forEach((entry) => {
          const date = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
          byDateMap[date] = {
            date: date,
            transactions: 0,
            revenue: 0,
            spend: entry.spend || 0,
            impressions: entry.impressions || 0,
            uniqueIdentifiers: new Set(),
          };
        });
      }

      // Overlay attribution-filtered transactions on top of the seeded map.
      allTransactionsData.forEach((transaction) => {
        const date = moment(transaction.transactionDate).format('YYYY-MM-DD');

        if (!byDateMap[date]) {
          byDateMap[date] = {
            date: date,
            transactions: 0,
            revenue: 0,
            spend: 0,
            impressions: 0,
            uniqueIdentifiers: new Set(),
          };
        }

        byDateMap[date].transactions += 1;
        byDateMap[date].revenue += transaction.revenue || 0;

        // Add unique identifier using attrValue
        if (transaction.attrValue) {
          byDateMap[date].uniqueIdentifiers.add(transaction.attrValue);
        }
      });

      byDateToBeProcessed = Object.values(byDateMap)
        .map((entry) => ({
          ...entry,
          customers: entry.uniqueIdentifiers.size,
          uniqueIdentifiers: Array.from(entry.uniqueIdentifiers),
        }))
        .sort((a, b) => moment(a.date).diff(moment(b.date)));

      // Create creatives data by date from allTransactionsData based on attribution filters
      const creativesByDateMap = {}; // Key: date_creativeId

      allTransactionsData.forEach((transaction) => {
        const transactionId = transaction.orderId;

        if (transaction.impressions && Array.isArray(transaction.impressions)) {
          transaction.impressions.forEach((impression) => {
            const creativeId = impression.creativeId;
            const date = moment(impression.impressionTime).format('YYYY-MM-DD');
            const key = `${date}_${creativeId}`;

            if (creativeId) {
              if (!creativesByDateMap[key]) {
                creativesByDateMap[key] = {
                  date: date,
                  creative: {
                    name: impression.creativeName || '',
                    media: impression.media || '',
                  },
                  impressions: 0,
                  transactions: 0,
                  transactionIds: new Set(),
                };
              }

              creativesByDateMap[key].impressions += 1;
              creativesByDateMap[key].transactionIds.add(transactionId);
            }
          });
        }
      });

      // Convert to flat array - each entry is a creative on a specific date
      creaivesToBeProcessed = Object.values(creativesByDateMap)
        .map((entry) => ({
          date: entry.date,
          creative: entry.creative,
          impressions: entry.impressions,
          transactions: entry.transactionIds.size,
        }))
        .sort((a, b) => moment(a.date).diff(moment(b.date)));

      // Create publishers data by date from allTransactionsData based on attribution filters
      const publishersByDateMap = {}; // Key: date_publisherId

      allTransactionsData.forEach((transaction) => {
        const transactionId = transaction.orderId;

        if (transaction.impressions && Array.isArray(transaction.impressions)) {
          transaction.impressions.forEach((impression) => {
            const publisherId = impression.impressionPublisher;
            const date = moment(impression.impressionTime).format('YYYY-MM-DD');
            const key = `${date}_${publisherId}`;

            if (publisherId) {
              if (!publishersByDateMap[key]) {
                publishersByDateMap[key] = {
                  date: date,
                  publisher: {
                    name: impression.impressionPublisher,
                    media: impression.impressionPublisherMedia || '',
                  },
                  impressions: 0,
                  transactions: 0,
                  transactionIds: new Set(),
                };
              }

              publishersByDateMap[key].impressions += 1;
              publishersByDateMap[key].transactionIds.add(transactionId);
            }
          });
        }
      });

      // Convert to flat array - each entry is a publisher on a specific date
      publisherToBeProcessed = Object.values(publishersByDateMap)
        .map((entry) => ({
          date: entry.date,
          publisher: entry.publisher,
          impressions: entry.impressions,
          transactions: entry.transactionIds.size,
        }))
        .sort((a, b) => moment(a.date).diff(moment(b.date)));
    }
    if (byDateToBeProcessed) {
      let dataToProcess = byDateToBeProcessed;
      // The campaign's OWN flight window, straight from the campaign order.
      // These feed the summary's startDate/endDate, which the KPI bar renders
      // as the "Flight Dates" card and its "% elapsed" progress bar — so they
      // must stay pinned to the campaign's configured dates. They were once
      // reassigned to the active filter range below, which relabelled the
      // flight card with whatever the user had picked.
      const flightStartDate = campaignOrder.startDate;
      const flightEndDate = campaignOrder.endDate;

      // Picker default — campaign flight start → today (see
      // core/utils/campaignDateRange). Anchored to the campaign's own flight so
      // switching the campaign sub-tab does not move it: "All" spans every
      // campaign in the order and starts earlier than any single one. Row dates
      // are still passed so an attribution tail past the flight end stays in
      // range.
      //
      // Never derived from the active selection. Narrowing it to the current
      // pick made "All Time" resolve to the range already selected (a no-op)
      // and clamped the calendar to it, so the user could not widen the range
      // again (#11680) — the resolver takes no selection argument, so that
      // cannot recur.
      const dateExtent = resolveCampaignDateRange(
        campaignOrder,
        (byDateToBeProcessed || []).map((row) => row && row.date)
      );
      if (dateExtent) {
        setDefaultSummaryDates(dateExtent);
      }

      // Filter data if date filter is active (URL params for "All" or local state for campaigns)
      if (activeStartDate && activeEndDate) {
        const startDate = moment(activeStartDate);
        const endDate = moment(activeEndDate);
        dataToProcess = byDateToBeProcessed.filter((dateEntry) => {
          const entryDate = moment(dateEntry.date);
          return (
            entryDate.isSameOrAfter(startDate, 'day') && entryDate.isSameOrBefore(endDate, 'day')
          );
        });
      }

      setByDate(dataToProcess);

      // Calculate summary based on campaign selection
      let summary;
      const hasDateFilter = activeStartDate && activeEndDate;

      if (campaignIndex !== 'All' && transactionsByCampaign?.length > 0) {
        // For specific campaign
        const campaign = transactionsByCampaign[parseInt(campaignIndex)];
        if (campaign) {
          if (attributionFiltered) {
            // When attribution filtered, use reconstructed dataToProcess which is already
            // filtered by both campaign (adProviderId) and attribution method/window
            summary = calculateSummary(dataToProcess, flightStartDate, flightEndDate);
          } else if (hasDateFilter) {
            // Filter campaign's byDate by selected date range
            const startDate = moment(activeStartDate);
            const endDate = moment(activeEndDate);
            const campaignByDateFiltered = (campaign.byDate || []).filter((d) => {
              const date = moment(d.date);
              return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
            });

            // Calculate summary from filtered byDate
            const totals = campaignByDateFiltered.reduce(
              (acc, d) => ({
                transactions: acc.transactions + (d.transactions || 0),
                transactionRevenue: acc.transactionRevenue + (d.revenue || 0),
                spend: acc.spend + (d.spend || 0),
                impressions: acc.impressions + (d.impressions || 0),
              }),
              { transactions: 0, transactionRevenue: 0, spend: 0, impressions: 0 }
            );

            // Get customers from the filtered dataToProcess (which has uniqueIdentifiers)
            const calculated = calculateSummary(dataToProcess, flightStartDate, flightEndDate);

            summary = {
              ...totals,
              customers: calculated.customers,
              startDate: flightStartDate,
              endDate: flightEndDate,
            };
          } else {
            // No date filter - use pre-aggregated values (existing behavior)
            summary = {
              transactions: campaign.transactions || 0,
              transactionRevenue: campaign.revenue || 0,
              spend: campaign.spend || 0,
              impressions: campaign.impressions || 0,
              customers: campaign.customers || 0,
              startDate: flightStartDate,
              endDate: flightEndDate,
            };
          }
        } else {
          summary = calculateSummary(dataToProcess, flightStartDate, flightEndDate);
        }
      } else {
        // Default calculation from byDate data
        summary = calculateSummary(dataToProcess, flightStartDate, flightEndDate);
      }

      if (campaignOrder?.pricingModel === 'CPM' && summary.impressions) {
        summary.spend = computeSpend(summary.impressions, getEffectiveCpm(campaignOrder));
      }

      setDetailBarSummary(summary);

      const dayOfWeekArray = calculateDayOfWeek(dataToProcess);
      setByDayOfWeek(dayOfWeekArray);
    }

    // Process creatives - use per-campaign data when specific campaign is selected
    if (campaignIndex !== 'All' && selectedCampaignCreatives?.length > 0) {
      // Transform per-campaign creatives data with date filtering
      const creativesMap = {};

      selectedCampaignCreatives.forEach((item) => {
        // Filter byDate if date range is selected
        let filteredByDate = item.byDate || [];
        const hasDateFilterCreatives = activeStartDate && activeEndDate;

        if (hasDateFilterCreatives) {
          const startDate = moment(activeStartDate);
          const endDate = moment(activeEndDate);
          filteredByDate = filteredByDate.filter((d) => {
            const date = moment(d.date);
            return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
          });
        }

        // Sum up filtered impressions/transactions from byDate
        const totals = filteredByDate.reduce(
          (acc, d) => ({
            impressions: acc.impressions + (d.impressions || 0),
            transactions: acc.transactions + (d.transactions || 0),
          }),
          { impressions: 0, transactions: 0 }
        );

        const name = item.creative?.name || '';
        if (!creativesMap[name]) {
          creativesMap[name] = {
            name: name,
            creativeId: item.creative?.creativeId || '',
            media: item.creative?.media || '',
            impressions: 0,
            transactions: 0,
          };
        }

        // Use totals from byDate if date filtered, otherwise use aggregate
        if (hasDateFilterCreatives) {
          creativesMap[name].impressions += totals.impressions;
          creativesMap[name].transactions += totals.transactions;
        } else {
          creativesMap[name].impressions += item.impressions || 0;
          creativesMap[name].transactions += item.transactions || 0;
        }
      });

      setCreatives(Object.values(creativesMap));
    } else if (creaivesToBeProcessed) {
      let creativesDataToProcess = creaivesToBeProcessed;

      // Filter creatives data if date filter is active
      if (activeStartDate && activeEndDate) {
        const startDate = moment(activeStartDate);
        const endDate = moment(activeEndDate);
        creativesDataToProcess = creaivesToBeProcessed.filter((dateEntry) => {
          const entryDate = moment(dateEntry.date);
          return (
            entryDate.isSameOrAfter(startDate, 'day') && entryDate.isSameOrBefore(endDate, 'day')
          );
        });
      }

      // Consolidate creatives by name
      const creativesMap = {};
      creativesDataToProcess.forEach((c) => {
        const name = c.creative.name;
        if (!creativesMap[name]) {
          creativesMap[name] = {
            name: name,
            creativeId: c.creative.creativeId || '',
            media: c.creative.media,
            impressions: 0,
            transactions: 0,
          };
        }
        creativesMap[name].impressions += c.impressions || 0;
        creativesMap[name].transactions += c.transactions || 0;
      });

      setCreatives(Object.values(creativesMap));
    } else if (creativesData) {
      // Consolidate by name - a single report can carry one creatives row per
      // campaign/adProvider, so the same creative appears multiple times.
      const creativesMap = {};
      creativesData.forEach((c) => {
        const name = c.name;
        if (!creativesMap[name]) {
          creativesMap[name] = {
            name: name,
            creativeId: c.creativeId || '',
            media: c.media,
            impressions: 0,
            transactions: 0,
          };
        }
        creativesMap[name].impressions += c.impressions || 0;
        creativesMap[name].transactions += c.transactions || 0;
      });

      setCreatives(Object.values(creativesMap));
    }

    // Process publishers - use per-campaign data when specific campaign is selected
    if (campaignIndex !== 'All' && selectedCampaignPublishers?.length > 0) {
      // Transform per-campaign publishers data with date filtering
      const publishersMap = {};

      selectedCampaignPublishers.forEach((item) => {
        // Filter byDate if date range is selected
        let filteredByDate = item.byDate || [];
        const hasDateFilterPublishers = activeStartDate && activeEndDate;

        if (hasDateFilterPublishers) {
          const startDate = moment(activeStartDate);
          const endDate = moment(activeEndDate);
          filteredByDate = filteredByDate.filter((d) => {
            const date = moment(d.date);
            return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
          });
        }

        // Sum up filtered impressions/transactions from byDate
        const totals = filteredByDate.reduce(
          (acc, d) => ({
            impressions: acc.impressions + (d.impressions || 0),
            transactions: acc.transactions + (d.transactions || 0),
          }),
          { impressions: 0, transactions: 0 }
        );

        const name = item.publisher?.name || '';
        if (!publishersMap[name]) {
          publishersMap[name] = {
            name: name,
            media: item.publisher?.media || '',
            impressions: 0,
            transactions: 0,
          };
        }

        // Use totals from byDate if date filtered, otherwise use aggregate
        if (hasDateFilterPublishers) {
          publishersMap[name].impressions += totals.impressions;
          publishersMap[name].transactions += totals.transactions;
        } else {
          publishersMap[name].impressions += item.impressions || 0;
          publishersMap[name].transactions += item.transactions || 0;
        }
      });

      setPublishers(Object.values(publishersMap));
    } else if (publisherToBeProcessed) {
      let publishersDataToProcess = publisherToBeProcessed;

      // Filter publishers data if date filter is active
      if (activeStartDate && activeEndDate) {
        const startDate = moment(activeStartDate);
        const endDate = moment(activeEndDate);
        publishersDataToProcess = publisherToBeProcessed.filter((dateEntry) => {
          const entryDate = moment(dateEntry.date);
          return (
            entryDate.isSameOrAfter(startDate, 'day') && entryDate.isSameOrBefore(endDate, 'day')
          );
        });
      }

      // Consolidate publishers by name
      const publishersMap = {};
      publishersDataToProcess.forEach((p) => {
        const name = p.publisher.name;
        if (!publishersMap[name]) {
          publishersMap[name] = {
            name: name,
            media: p.publisher.media,
            impressions: 0,
            transactions: 0,
          };
        }
        publishersMap[name].impressions += p.impressions || 0;
        publishersMap[name].transactions += p.transactions || 0;
      });

      setPublishers(Object.values(publishersMap));
    } else if (publishersData) {
      // Consolidate publishers by name for non-date filtered data - a single
      // report can carry one publishers row per campaign/adProvider, so the
      // same publisher appears multiple times.
      const publishersMap = {};
      publishersData.forEach((p) => {
        const name = p.name;
        if (!publishersMap[name]) {
          publishersMap[name] = {
            name: name,
            media: p.media,
            impressions: 0,
            transactions: 0,
          };
        }
        publishersMap[name].impressions += p.impressions || 0;
        publishersMap[name].transactions += p.transactions || 0;
      });

      setPublishers(Object.values(publishersMap));
    }

    if (transactionsData) {
      setTransactions(transactionsData?.edges?.map((edge) => edge.node) || []);
      setTransactionsPagination({
        total: pagination?.count || 0,
        hasNextPage: false,
      });
    }
  }, [activeByDate, creativesDataByDate, publishersDataByDate, attributionFiltered, allTransactionsData, creativesData, publishersData, transactionsData, byDateData, campaignOrder, campaignOrder.startDate, campaignOrder.endDate, activeStartDate, activeEndDate, campaignIndex, transactionsByCampaign, pagination, selectedCampaignCreatives, selectedCampaignPublishers]);

  // The export button spins while the full set is being fetched for the card
  // or an export is in progress.
  useEffect(() => {
    setExportButtonLoading(allTransactionsLoading || exporting);
  }, [setExportButtonLoading, allTransactionsLoading, exporting]);

  useEffect(() => {
    setExportButtonDisable(false);
    setExportButtonMenuItems([
      {
        key: 'transactionEventsSource',
        text: 'Transaction - With Events',
        tooltip:
          'This report lists transactions with Impression events for the selected date range.',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: async () => {
          // Bound to this click's own fetch: the loader pins the scope for the
          // whole walk, so a filter/tab change mid-export neither swaps the file
          // for the new scope's rows nor strands the export without a download.
          setExporting(true);
          try {
            const result = await getAllTransactions();
            if (!result || result.error) throw new Error('Full transactions fetch failed');
            await downloadTransactionsWithEvents(result.rows, campaignOrder.name);
          } catch (error) {
            console.error('Error exporting transactions:', error);
            message.error('Transactions export failed. Please try again.');
          } finally {
            setExporting(false);
          }
        },
      },
      {
        key: 'transactionEventsDaily',
        text: 'Transaction - With Events (Daily Total)',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: () => {
          setExportButtonLoading(true);
          setExporting(true);
          const zip = new JSZip();
          const folder = zip.folder(`Daily Total`);

          folder.file(
            `${campaignOrder.name} Daily Total.csv`,
            Papa.unparse(
              byDate.map((item) => ({
                Date: moment(item.date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
                Transactions: item.transactions,
                Revenue: formatCurrency2SigFig(item.revenue),
                Spend: formatCurrency2SigFig(item.spend),
                Impressions: item.impressions,
                Customers: item.customers,
              }))
            )
          );
          zip.generateAsync({ type: 'blob' }).then(
            (blob) => {
              saveAs(blob, `${campaignOrder.name.replace(/\//g, ' ')} - Daily`);
              return true;
            },
            function (e) {
              console.log('error', e);
              return false;
            }
          );
          setExportButtonLoading(false);
          setExporting(false);
        },
      },
      {
        key: 'transactionEventsCreatives',
        text: 'Transaction - With Events - (Creatives)',
        tooltip:
          'This report lists transactions with Impression events for the selected date range.',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: () => {
          setExportButtonLoading(true);
          setExporting(true);
          const zip = new JSZip();
          const folder = zip.folder(`Creatives`);

          folder.file(
            `${campaignOrder.name} Creatives.csv`,
            Papa.unparse(formatCreativesExport(creatives))
          );
          zip.generateAsync({ type: 'blob' }).then(
            (blob) => {
              saveAs(blob, `${campaignOrder.name.replace(/\//g, ' ')} - Creatives`);
              return true;
            },
            function (e) {
              console.log('error', e);
              return false;
            }
          );
          setExportButtonLoading(false);
          setExporting(false);
        },
      },
      {
        key: 'transactionEventsPublishers',
        text: 'Transaction - With Events - (Publishers)',
        tooltip:
          'This report lists transactions with Impression events for the selected date range.',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: () => {
          setExportButtonLoading(true);
          setExporting(true);
          const zip = new JSZip();
          const folder = zip.folder(`Publishers`);

          folder.file(
            `${campaignOrder.name} Publisher.csv`,
            Papa.unparse(formatPublishersExport(publishers))
          );
          zip.generateAsync({ type: 'blob' }).then(
            (blob) => {
              saveAs(blob, `${campaignOrder.name.replace(/\//g, ' ')} - Publishers`);
              return true;
            },
            function (e) {
              console.log('error', e);
              return false;
            }
          );
          setExportButtonLoading(false);
          setExporting(false);
        },
      },
    ]);
  }, [
    byDate,
    campaignOrder.name,
    creatives,
    getAllTransactions,
    publishers,
    setExportButtonDisable,
    setExportButtonLoading,
    setExportButtonMenuItems,
  ]);

  const changeDatePicker = (dates) => {
    if (!dates || dates.length !== 2) return;

    const [startDate, endDate] = dates;

    // Always update URL params (needed for transactions table query in Loader)
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('startDate', startDate.format('YYYY-MM-DD'));
    queryParams.set('endDate', endDate.format('YYYY-MM-DD'));
    queryParams.set('page', '1'); // Reset to first page when filtering
    history.push({ search: queryParams.toString() });

    // For per-campaign tabs, also save to local state so we can restore when switching back
    if (campaignIndex !== 'All') {
      setCampaignDateRanges((prev) => ({
        ...prev,
        [campaignIndex]: [startDate, endDate],
      }));
    }
  };

  const changeAttributionWindow = (attributionWindow) => {
    // Update URL params with attribution window filter
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('attributionWindow', attributionWindow);
    queryParams.set('page', '1'); // Reset to first page when filtering
    history.push({ search: queryParams.toString() });
  };

  const changeAttributionMethod = (attributionMethod) => {
    // Update URL params with attribution method filter
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('attributionMethod', attributionMethod);
    queryParams.set('page', '1'); // Reset to first page when filtering
    history.push({ search: queryParams.toString() });
  };

  // Fetch the full filtered set whenever a filter is active and the loader's
  // fetch scope (report, date range, attribution filters, campaign) changes.
  // The set is scoped server-side, so any scope change needs a new fetch.
  useEffect(() => {
    if (attributionFiltered) {
      getAllTransactions();
    }
  }, [attributionFiltered, transactionsScopeKey, getAllTransactions]);

  return React.cloneElement(props.children, {
    byDate,
    byDayOfWeek,
    defaultSummaryDates,
    creatives,
    publishers,
    transactions,
    transactionsPagination,
    getImpressions,
    detailBarSummary,
    changeDatePicker,
    changeAttributionWindow,
    attributionWindow,
    changeAttributionMethod,
    attributionMethod,
    campaignOrder,
    // Individual loading states for each component. While a filter is active
    // the Performance card is computed from the full filtered set, so it is
    // loading until that set exists for the current scope; otherwise it would
    // briefly show the unfiltered rollup numbers.
    byDateLoading:
      byDateLoading ||
      campaignByDateLoading ||
      (attributionFiltered &&
        !allTransactionsError &&
        (allTransactionsLoading || allTransactionsData == null)),
    // A failed fetch of the filtered set is surfaced as an error with a retry,
    // never as zeros (which would read as "nothing survived the filter").
    byDateError: attributionFiltered && Boolean(allTransactionsError),
    retryByDate: getAllTransactions,
    creativesLoading: creativesLoading || creativesByDateLoading,
    publishersLoading: publishersLoading || publishersByDateLoading,
    transactionsLoading,
    detailsLoading,
    // Pass by-date loading states separately for header indicators
    creativesByDateLoading,
    publishersByDateLoading,
    // Campaign tab state and data
    campaignIndex,
    setCampaignIndex,
    transactionsByCampaign,
    showCampaignTabs: transactionsByCampaign?.length > 1,
    campaignByDateLoading,
  });
};

export default TransactionsTabController;
